/* eslint-disable no-unused-vars */
import { useSelector } from 'react-redux';
import { useFacade } from './useFacade';
import { Loader, Chart } from 'components';
import { StatisticBtn } from './components/statisticBtn/StatisticBtn';
import { ChartError } from './components/ChartError';

import { Palette } from 'config';
import * as S from './StatisticAndChart.styled';

const colors = {
  installs: {
    main: Palette.Fuchsia._500,
    background: Palette.Fuchsia._100,
  },
  impressions: {
    main: Palette.LightBlue._500,
    background: Palette.LightBlue._100,
  },
  clicks: {
    main: Palette.Violet._500,
    background: Palette.Violet._100,
  },
  cost: {
    main: Palette.Amber._500,
    background: Palette.Amber._100,
  },
  cr: {
    main: Palette.Pink._500,
    background: Palette.Pink._100,
  },
  avg_cpi: {
    main: Palette.Teal._500,
    background: Palette.Teal._100,
  },
};

// list all metrics to map over later in the code (order matters)
const metrics = Object.keys(colors); // ['installs', 'impressions', 'clicks', 'cost', 'cr', 'avg_cpi'];

export const StatisticAndChart = ({ timePeriod }) => {
  const {
    isFetchingDataChart,
    isFetchingPrevDataChart,
    isError,
    dataPerDay,
    totalValues,
    numOfDays,
    change,
    errorMsg,
    isZeroData,
    isChartHidden,
    selectedMetrics,
  } = useFacade({ timePeriod });

  if (isFetchingDataChart || (!dataPerDay?.size && !isError)) {
    return (
      <S.LoaderWrap>
        <Loader.Local delay={0} />
      </S.LoaderWrap>
    );
  }

  if ((isError && !isFetchingDataChart) || isZeroData) {
    return (
      <S.ErrorContainer>
        <ChartError message={errorMsg} />
      </S.ErrorContainer>
    );
  }

  return (
    !!dataPerDay?.size && (
      <S.Container>
        <S.StatisticBox>
          <StatisticBtn
            label='Installs'
            value={formatNumber(totalValues.installs)}
            avg={formatNumber(totalValues.installs / numOfDays)}
            change={change.installs}
            numOfDays={numOfDays}
            color={colors.installs}
            metric='installs'
            isFetchingPrevDataChart={isFetchingPrevDataChart}
            selectedMetrics={selectedMetrics}
            isChartHidden={isChartHidden}
          />
          <StatisticBtn
            label='Impressions'
            avg={formatNumber(totalValues.impressions / numOfDays)}
            change={change.impressions}
            numOfDays={numOfDays}
            value={formatNumber(totalValues.impressions)}
            color={colors.impressions}
            metric='impressions'
            isFetchingPrevDataChart={isFetchingPrevDataChart}
            selectedMetrics={selectedMetrics}
            isChartHidden={isChartHidden}
          />
          <StatisticBtn
            label='Clicks'
            avg={formatNumber(totalValues.clicks / numOfDays)}
            change={change.clicks}
            numOfDays={numOfDays}
            value={formatNumber(totalValues.clicks)}
            color={colors.clicks}
            metric='clicks'
            isFetchingPrevDataChart={isFetchingPrevDataChart}
            selectedMetrics={selectedMetrics}
            isChartHidden={isChartHidden}
          />
          <StatisticBtn
            label='Cost'
            avg={formatNumber(totalValues.cost / numOfDays)}
            change={change.cost}
            numOfDays={numOfDays}
            value={`$${formatNumber(totalValues.cost)}`}
            color={colors.cost}
            metric='cost'
            isFetchingPrevDataChart={isFetchingPrevDataChart}
            selectedMetrics={selectedMetrics}
            isChartHidden={isChartHidden}
          />
          <StatisticBtn
            label='Conversion Rate'
            change={change.cr}
            numOfDays={numOfDays}
            value={`${formatNumber(totalValues.cr)}%`}
            color={colors.cr}
            metric='cr'
            isFetchingPrevDataChart={isFetchingPrevDataChart}
            selectedMetrics={selectedMetrics}
            isChartHidden={isChartHidden}
          />
          <StatisticBtn
            label='AVG CPI'
            change={change.avg_cpi}
            numOfDays={numOfDays}
            value={`$${formatNumber(totalValues.avg_cpi)}`}
            color={colors.avg_cpi}
            metric='avg_cpi'
            isFetchingPrevDataChart={isFetchingPrevDataChart}
            selectedMetrics={selectedMetrics}
            isChartHidden={isChartHidden}
          />
        </S.StatisticBox>
        <Chart
          dataPerDay={dataPerDay}
          colors={colors}
          metrics={metrics}
          selectedMetrics={selectedMetrics}
          selectedMetricsSelector='manageCampaigns.selectedMetrics'
          isChartHidden={isChartHidden}
          withNotes={true}
          withModal={true}
        />
      </S.Container>
    )
  );
};

function formatNumber(number) {
  const suffixes = ['', 'K', 'M', 'B']; // thousands, millions, billions

  const suffixIndex = Math.floor(Math.log10(number) / 3);
  const scaledNumber = suffixes[suffixIndex] ? number / Math.pow(10, suffixIndex * 3) : number;
  let formattedNumber = scaledNumber?.toFixed(2);

  if (formattedNumber?.indexOf('.') !== -1) {
    formattedNumber = formattedNumber?.replace(/\.?0*$/, '');
  }

  return formattedNumber + (suffixes[suffixIndex] ? suffixes[suffixIndex] : '');
}
